import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Share2, Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { fetchPosts } from "../../../../redux/blogSlice";
import { AppDispatch, RootState } from "../../../../redux/store";
import hqImg from "../../../../assets/app/HQ.png";


/* ─────────────────────────────────────────────────────────
   EDITORIAL LUXURY THEME
   White paper · Cormorant display · Montserrat body
   Gold rule accent (#bf9b30) · Ink black (#0a0a0a)
───────────────────────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,600&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

const C = {
  paper:    "#fafaf8",
  white:    "#ffffff",
  ink:      "#0a0a0a",
  inkSoft:  "#3a3a3a",
  inkMid:   "#6b6b6b",
  rule:     "#d4c8a8",
  gold:     "#bf9b30",
  goldLight:"#e8c98a",
  border:   "#e8e4da",
  serif:    "'Cormorant Garamond', Georgia, serif",
  sans:     "'Montserrat', sans-serif",
};

const News: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { posts } = useSelector((state: RootState) => state.blog);
  const sortedPosts = [...posts].sort((a, b) => {
    const dateA = new Date(a.createdAt?.seconds ? a.createdAt.seconds * 1000 : a.createdAt);
    const dateB = new Date(b.createdAt?.seconds ? b.createdAt.seconds * 1000 : b.createdAt);
    return dateB.getTime() - dateA.getTime();
  });

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!posts.length) dispatch(fetchPosts());
  }, [dispatch, posts.length]);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const handleShare = (post: any) => {
    const url = `${window.location.origin}/${post.slug}`;
    if (navigator.share) {
      navigator.share({ title: post.title, text: post.description, url });
    } else {
      navigator.clipboard.writeText(url);
      alert("Link copied to clipboard");
    }
  };

  const handleScrollDown = () => {
    document.getElementById("news-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const year = new Date().getFullYear();
  const visiblePosts = sortedPosts.slice(0, 8);
  const [featured, ...restPosts] = visiblePosts;

  const fmtDate = (raw: any) => {
    if (!raw) return "";
    const d = new Date(typeof raw === "string" ? raw : raw.seconds * 1000);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  };

  return (
    <div style={{ background: C.paper, minHeight: "100vh", fontFamily: C.sans, color: C.ink }}>
      <style>{FONT_CSS + PAGE_CSS}</style>

      {/* ══════════════════════════════════
          HEADER
      ══════════════════════════════════ */}
      <header style={{
        ...S.header,
        background: scrolled ? "rgba(250,250,248,0.97)" : "transparent",
        borderBottom: scrolled ? `1px solid ${C.border}` : "1px solid transparent",
        boxShadow: scrolled ? "0 1px 20px rgba(0,0,0,0.06)" : "none",
      }}>
        <button style={S.menuBtn} onClick={() => setMenuOpen(true)}>
          <Menu size={22} color={scrolled ? C.ink : "#fff"} />
        </button>

        <div style={S.wordmark}>
          <span style={{ ...S.wordmarkText, color: scrolled ? C.ink : "#fff" }}>4REVA GROUP</span>
          <span style={{ ...S.wordmarkSub, color: scrolled ? C.gold : "rgba(255,255,255,0.7)" }}>NEWS</span>
        </div>

        <button onClick={() => navigate("/MoreNews")} style={{
          ...S.allBtn,
          color: scrolled ? C.inkMid : "rgba(255,255,255,0.8)",
          borderColor: scrolled ? C.border : "rgba(255,255,255,0.3)",
        }}>
          All Stories
        </button>
      </header>

      {/* ══════════════════════════════════
          SIDE MENU OVERLAY
      ══════════════════════════════════ */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={S.menuOverlay}
            onClick={() => setMenuOpen(false)}
          >
            <motion.div
              initial={{ x: -320 }} animate={{ x: 0 }} exit={{ x: -320 }}
              transition={{ ease: [0.4, 0, 0.2, 1], duration: 0.35 }}
              style={S.menuPanel}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ height: 3, background: `linear-gradient(to right, ${C.gold}, ${C.goldLight}, transparent)`, marginBottom: 40 }} />

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 48 }}>
                <div>
                  <div style={{ fontFamily: C.serif, fontSize: 28, fontWeight: 600, color: C.ink, letterSpacing: "0.04em", lineHeight: 1 }}>4REVA GROUP</div>
                  <div style={{ fontSize: 10, color: C.gold, letterSpacing: "0.3em", marginTop: 4 }}>NEWS</div>
                </div>
                <button onClick={() => setMenuOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                  <X size={22} color={C.inkMid} />
                </button>
              </div>

              {[
                { label: "Latest News",       path: "/News" },
                { label: "All Stories",        path: "/MoreNews" },
                { label: "Technology",         path: "/News" },
                { label: "Innovation",         path: "/News" },
                { label: "About 4REVA GROUP",  path: "/pages/about-us" },
              ].map((item, i) => (
                <motion.button
                  key={item.label}
                  initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                  onClick={() => { navigate(item.path); setMenuOpen(false); }}
                  style={S.menuItem}
                >
                  {item.label}
                  <ArrowRight size={14} color={C.gold} style={{ opacity: 0.7 }} />
                </motion.button>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════════════════════════
          HERO — HQ.png full-bleed image
      ══════════════════════════════════ */}
      <div style={S.heroWrap}>
        {/* HQ image as hero background */}
        <img src={hqImg} alt="4REVA GROUP HQ" style={S.heroImg} />

        {/* Dark gradient overlay */}
        <div style={S.heroGradient} />

        {/* Content */}
        <div style={S.heroContent}>
          <motion.div
            initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
            transition={{ duration: 1.1, ease: [0.4, 0, 0.2, 1] }}
            style={{ width: 60, height: 1, background: C.gold, transformOrigin: "left", marginBottom: 24 }}
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }}
            style={S.heroEyebrow}>
            TECHNOLOGY · INNOVATION · DIGITAL VENTURES
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.8 }}
            style={S.heroTitle}>
            Ideas That Move<br />
            <em style={{ fontStyle: "italic", fontWeight: 300 }}>The World Forward</em>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
            style={S.heroSub}>
            Insights, updates and stories from across the 4REVA GROUP portfolio — London, UK
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.6 }}
            style={{ display: "flex", gap: 14, marginTop: 32 }}>
            <button style={S.heroCtaPrimary} onClick={() => navigate("/about")}>
              About Us
            </button>
            <button style={S.heroCtaSecondary} onClick={handleScrollDown}>
              Read Stories
            </button>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.6 }}
          onClick={handleScrollDown} style={S.scrollBtn}>
          <ChevronDown size={20} color="rgba(255,255,255,0.7)" className="bounce" />
        </motion.div>
      </div>

      {/* ══════════════════════════════════
          EDITION LINE
      ══════════════════════════════════ */}
      <div style={S.editionBar}>
        <div style={S.editionRule} />
        <span style={S.editionText}>4REVA GROUP NEWS · {new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" }).toUpperCase()}</span>
        <div style={S.editionRule} />
      </div>

      {/* ══════════════════════════════════
          FEATURED STORY
      ══════════════════════════════════ */}
      {featured && (
        <div style={S.featuredWrap}>
          <div style={S.featuredImageCol}>
            <div style={S.featuredImageWrap}>
              <img src={featured.featuredImage} alt={featured.title} style={S.featuredImage} />
              <div style={S.featuredImageOverlay} />
            </div>
          </div>
          <div style={S.featuredTextCol}>
            <div style={S.featuredRule} />
            <span style={S.featuredTag}>{featured.category || "FEATURED"}</span>
            <h2 style={S.featuredTitle}>{featured.title}</h2>
            <p style={S.featuredDesc}>{featured.description}</p>
            <div style={S.featuredMeta}>
              <span style={S.featuredDate}>{fmtDate(featured.createdAt)}</span>
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
              <button style={S.featuredCta} onClick={() => navigate(`/${featured.slug}`)}>
                Read Story <ArrowRight size={14} style={{ marginLeft: 6 }} />
              </button>
              <button style={S.featuredShare} onClick={() => handleShare(featured)}>
                <Share2 size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════
          NEWS GRID
      ══════════════════════════════════ */}
      <div id="news-section" style={S.gridSection}>
        <div style={S.gridHeader}>
          <div style={S.gridRule} />
          <h2 style={S.gridHeading}>Latest Stories</h2>
          <div style={S.gridRule} />
        </div>

        <div style={S.grid}>
          {restPosts.map((post, i) => {
            const createdAt = post.createdAt
              ? new Date(typeof post.createdAt === "string" ? post.createdAt : post.createdAt.seconds * 1000)
              : null;

            return (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
                style={S.card}
                className="news-card"
              >
                <div style={S.cardImgWrap}>
                  <img src={post.featuredImage} alt={post.title} style={S.cardImg} />
                  <div style={S.cardBadge}>{post.category || "NEWS"}</div>
                  <button style={S.cardShare} onClick={() => handleShare(post)}>
                    <Share2 size={13} color="#fff" />
                  </button>
                </div>

                <div style={S.cardBody}>
                  <div style={S.cardGoldRule} />
                  <h3 style={S.cardTitle}>{post.title}</h3>
                  <p style={S.cardDesc}>{post.description}</p>
                  <div style={S.cardFooter}>
                    {createdAt && (
                      <span style={S.cardDate}>
                        {createdAt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    )}
                    <button style={S.cardBtn} onClick={() => navigate(`/${post.slug}`)}>
                      Read <ArrowRight size={12} style={{ marginLeft: 4 }} />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div style={{ textAlign: "center", marginTop: 56 }}>
          <button style={S.seeAllBtn} onClick={() => navigate("/MoreNews")}>
            See All Stories <ArrowRight size={15} style={{ marginLeft: 8 }} />
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════
          EXPLORE / FOOTER LINKS
      ══════════════════════════════════ */}
      <div style={S.exploreSection}>
        <div style={S.exploreDivider} />
        <h2 style={S.exploreTitle}>Explore</h2>
        <div style={S.exploreGrid}>
          <div style={S.exploreCol}>
            <h4 style={S.exploreColHead}>About Us</h4>
            <ul style={S.exploreList}>
              <li onClick={() => navigate("/pages/about-us")}>About us</li>
              <li onClick={() => navigate("/pages/support")}>Contact & Support</li>
              <li onClick={() => navigate("/pages/privacy")}>Privacy & Policy</li>
              <li onClick={() => navigate("/pages/terms")}>Terms</li>
              <li onClick={() => navigate("/services")}>Our Services</li>
              <li onClick={() => navigate("/testimonials")}>Testimonials</li>
            </ul>
          </div>

          <div style={S.exploreCol}>
            <h4 style={S.exploreColHead}>Our Ventures</h4>
            <ul style={S.exploreList}>
              <li onClick={() => navigate("/companies")}>Portfolio Companies</li>
              <li onClick={() => navigate("/events")}>Events</li>
              <li onClick={() => navigate("/hospitality")}>Partnerships</li>
            </ul>
          </div>

          <div style={S.exploreCol}>
            <h4 style={S.exploreColHead}>Discover</h4>
            <ul style={S.exploreList}>
              <li onClick={() => navigate("/partners")}>Our Partners</li>
              <li onClick={() => navigate("/events")}>Upcoming Events</li>
              <li onClick={() => navigate("/News")}>Blog</li>
              <li onClick={() => navigate("/careers")}>Careers</li>
            </ul>
          </div>

          <div style={S.exploreCol}>
            <h4 style={S.exploreColHead}>Support</h4>
            <ul style={S.exploreList}>
              <li onClick={() => navigate("/faq")}>FAQs</li>
              <li onClick={() => navigate("/contact-us")}>Live Chat</li>
              <li onClick={() => navigate("/pages/help-support")}>Help Center</li>
              <li onClick={() => navigate("/feedback")}>Send Feedback</li>
            </ul>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════
          FOOTER
      ══════════════════════════════════ */}
      <footer style={S.footer}>
        <div style={{ height: 1, background: `linear-gradient(to right, transparent, ${C.gold}, transparent)`, marginBottom: 20 }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ fontFamily: C.serif, fontSize: 18, fontWeight: 600, letterSpacing: "0.12em", color: C.ink }}>
            4REVA GROUP <span style={{ color: C.gold }}>NEWS</span>
          </div>
          <span style={{ fontSize: 12, color: C.inkMid, letterSpacing: "0.08em" }}>© 4REVA GROUP {year}</span>
          <div style={{ display: "flex", gap: 20 }}>
            {["Privacy", "Terms", "Contact"].map(l => (
              <span key={l} style={{ fontSize: 11, color: C.inkMid, cursor: "pointer", letterSpacing: "0.1em" }}
                onClick={() => navigate(`/pages/${l.toLowerCase()}`)}>
                {l.toUpperCase()}
              </span>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default News;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {

  /* ── Header ── */
  header: {
    position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "0 28px", height: 62,
    backdropFilter: "blur(14px)",
    transition: "background 0.35s, border-bottom 0.35s, box-shadow 0.35s",
  },
  menuBtn:      { background: "none", border: "none", cursor: "pointer", padding: 6, display: "flex" },
  wordmark:     { position: "absolute", left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "baseline", gap: 6 },
  wordmarkText: { fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 600, letterSpacing: "0.28em", transition: "color 0.35s" },
  wordmarkSub:  { fontFamily: "'Montserrat', sans-serif", fontSize: 9, fontWeight: 600, letterSpacing: "0.4em", transition: "color 0.35s" },
  allBtn: {
    background: "none", fontSize: 10, letterSpacing: "0.14em", fontWeight: 500,
    padding: "7px 14px", cursor: "pointer",
    fontFamily: "'Montserrat', sans-serif",
    border: "1px solid",
    transition: "color 0.3s, border-color 0.3s",
  },

  /* ── Side menu ── */
  menuOverlay: {
    position: "fixed", inset: 0, zIndex: 500,
    background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
  },
  menuPanel: {
    position: "absolute", top: 0, left: 0, bottom: 0,
    width: 300, background: C.paper,
    boxShadow: "4px 0 40px rgba(0,0,0,0.15)",
    padding: "0 32px 40px",
    display: "flex", flexDirection: "column",
    overflowY: "auto",
  },
  menuItem: {
    background: "none", border: "none", borderBottom: `1px solid ${C.border}`,
    padding: "16px 0", width: "100%", textAlign: "left",
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 20, fontWeight: 500, color: C.ink,
    cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center",
    letterSpacing: "0.02em",
  },

  /* ── Hero ── */
  heroWrap: { position: "relative", width: "100%", height: "100vh", overflow: "hidden" },

  /* HQ image replaces the video */
  heroImg: {
    position: "absolute", inset: 0,
    width: "100%", height: "100%",
    objectFit: "cover", objectPosition: "center",
    zIndex: 1,
  },

  heroGradient: {
    position: "absolute", inset: 0, zIndex: 2,
    background: "linear-gradient(160deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.42) 55%, rgba(0,0,0,0.68) 100%)",
  },
  heroContent: {
    position: "absolute", inset: 0, zIndex: 3,
    display: "flex", flexDirection: "column", justifyContent: "center",
    padding: "0 7vw", maxWidth: 800,
  },
  heroEyebrow: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 10, fontWeight: 500, letterSpacing: "0.35em",
    color: C.gold, marginBottom: 14,
  },
  heroTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "clamp(40px,6vw,80px)",
    fontWeight: 600, lineHeight: 1.06,
    color: "#fff", margin: 0, letterSpacing: "-0.01em",
  },
  heroSub: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 14, fontWeight: 300, color: "rgba(255,255,255,0.72)",
    maxWidth: 460, marginTop: 18, lineHeight: 1.7, letterSpacing: "0.03em",
  },
  heroCtaPrimary: {
    background: `linear-gradient(135deg, ${C.gold}, #e8c98a)`,
    color: "#000", border: "none",
    padding: "13px 32px", fontSize: 11, fontWeight: 700,
    letterSpacing: "0.18em", cursor: "pointer",
    fontFamily: "'Montserrat', sans-serif",
  },
  heroCtaSecondary: {
    background: "transparent",
    color: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(255,255,255,0.3)",
    padding: "13px 28px", fontSize: 11, fontWeight: 500,
    letterSpacing: "0.14em", cursor: "pointer",
    fontFamily: "'Montserrat', sans-serif",
  },
  scrollBtn: {
    position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)",
    zIndex: 3, cursor: "pointer",
    width: 44, height: 44, borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.25)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },

  /* ── Edition bar ── */
  editionBar: {
    display: "flex", alignItems: "center", gap: 16,
    padding: "22px 40px",
    borderBottom: `1px solid ${C.border}`,
  },
  editionRule: { flex: 1, height: 1, background: C.rule },
  editionText: {
    fontSize: 9, letterSpacing: "0.32em", color: C.gold,
    fontFamily: "'Montserrat', sans-serif", fontWeight: 600, whiteSpace: "nowrap",
  },

  /* ── Featured ── */
  featuredWrap: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 0,
    maxWidth: 1300, margin: "0 auto",
    padding: "64px 40px",
    borderBottom: `1px solid ${C.border}`,
  },
  featuredImageCol:    { paddingRight: 48 },
  featuredImageWrap:   { position: "relative", overflow: "hidden", height: 480 },
  featuredImage:       { width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.6s ease", display: "block" },
  featuredImageOverlay:{ position: "absolute", bottom: 0, left: 0, right: 0, height: 80, background: "linear-gradient(to top, rgba(0,0,0,0.15), transparent)" },
  featuredTextCol:     { display: "flex", flexDirection: "column", justifyContent: "center", paddingLeft: 48, borderLeft: `1px solid ${C.border}` },
  featuredRule:        { width: 40, height: 2, background: C.gold, marginBottom: 22 },
  featuredTag:         { fontSize: 9, letterSpacing: "0.3em", fontWeight: 600, color: C.gold, fontFamily: "'Montserrat', sans-serif", marginBottom: 16 },
  featuredTitle:       { fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(26px,3.2vw,42px)", fontWeight: 600, lineHeight: 1.12, color: C.ink, margin: 0, letterSpacing: "-0.01em" },
  featuredDesc:        { fontSize: 14, lineHeight: 1.8, color: C.inkSoft, fontFamily: "'Montserrat', sans-serif", fontWeight: 300, marginTop: 18 },
  featuredMeta:        { marginTop: 20, display: "flex", alignItems: "center", gap: 12 },
  featuredDate:        { fontSize: 11, color: C.inkMid, letterSpacing: "0.08em" },
  featuredCta:         { display: "inline-flex", alignItems: "center", background: C.ink, color: "#fff", border: "none", padding: "12px 24px", fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", cursor: "pointer", fontFamily: "'Montserrat', sans-serif" },
  featuredShare:       { background: "transparent", border: `1px solid ${C.border}`, width: 42, height: 42, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },

  /* ── Grid section ── */
  gridSection: { maxWidth: 1300, margin: "0 auto", padding: "64px 40px" },
  gridHeader:  { display: "flex", alignItems: "center", gap: 20, marginBottom: 48 },
  gridRule:    { flex: 1, height: 1, background: C.rule },
  gridHeading: { fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 600, color: C.ink, margin: 0, letterSpacing: "0.04em", whiteSpace: "nowrap" },
  grid:        { display: "grid", gap: 32, gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" },

  /* ── Card ── */
  card:         { background: C.white, border: `1px solid ${C.border}`, overflow: "hidden", transition: "box-shadow 0.3s, transform 0.3s" },
  cardImgWrap:  { position: "relative", height: 200, overflow: "hidden" },
  cardImg:      { width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.5s ease" },
  cardBadge:    { position: "absolute", top: 14, left: 14, background: C.gold, color: "#000", fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", padding: "4px 10px", fontFamily: "'Montserrat', sans-serif" },
  cardShare:    { position: "absolute", top: 12, right: 12, background: "rgba(0,0,0,0.55)", border: "none", width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  cardBody:     { padding: "22px 22px 18px" },
  cardGoldRule: { width: 28, height: 1.5, background: C.gold, marginBottom: 14 },
  cardTitle:    { fontFamily: "'Cormorant Garamond', serif", fontSize: 21, fontWeight: 600, color: C.ink, lineHeight: 1.22, marginBottom: 10, letterSpacing: "-0.005em" },
  cardDesc:     { fontSize: 12, lineHeight: 1.75, color: C.inkMid, fontFamily: "'Montserrat', sans-serif", fontWeight: 300, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" },
  cardFooter:   { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.border}` },
  cardDate:     { fontSize: 10, color: C.inkMid, letterSpacing: "0.06em" },
  cardBtn:      { display: "inline-flex", alignItems: "center", background: "transparent", border: "none", color: C.gold, fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", cursor: "pointer", fontFamily: "'Montserrat', sans-serif", padding: 0 },

  /* ── See All ── */
  seeAllBtn: {
    display: "inline-flex", alignItems: "center",
    background: "transparent", border: `1px solid ${C.ink}`,
    color: C.ink, padding: "14px 40px",
    fontSize: 11, fontWeight: 500, letterSpacing: "0.2em",
    cursor: "pointer", fontFamily: "'Montserrat', sans-serif",
    transition: "background 0.2s, color 0.2s",
  },

  /* ── Explore ── */
  exploreSection:  { background: C.ink, color: "#fff", padding: "64px 40px" },
  exploreDivider:  { height: 2, background: `linear-gradient(to right, ${C.gold}, ${C.goldLight}, transparent)`, marginBottom: 40 },
  exploreTitle:    { fontFamily: "'Cormorant Garamond', serif", fontSize: 38, fontWeight: 600, color: C.gold, marginBottom: 40, letterSpacing: "0.04em", textAlign: "center" },
  exploreGrid:     { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 40 },
  exploreCol:      { flex: "1 1 200px", textAlign: "left" },
  exploreColHead:  { fontFamily: "'Montserrat', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.25em", color: C.gold, marginBottom: 20, textTransform: "uppercase" },
  exploreList:     { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12, color: "rgba(255,255,255,0.65)", fontSize: 13, cursor: "pointer", fontFamily: "'Montserrat', sans-serif", fontWeight: 300, lineHeight: 1.5 },

  /* ── Footer ── */
  footer: { background: C.paper, padding: "24px 40px", borderTop: `1px solid ${C.border}` },
};

/* ─────────────────────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────────────────────── */
const PAGE_CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { width: 0; height: 0; }

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(5px); }
  }
  .bounce { animation: bounce 1.6s ease-in-out infinite; }

  .news-card:hover { box-shadow: 0 8px 40px rgba(0,0,0,0.1); transform: translateY(-3px); }
  .news-card:hover img { transform: scale(1.04); }

  @media (max-width: 900px) {
    div[style*="gridTemplateColumns: 1fr 1fr"] {
      grid-template-columns: 1fr !important;
      gap: 32px !important;
    }
    div[style*="paddingLeft: 48"] {
      padding-left: 0 !important;
      border-left: none !important;
      border-top: 1px solid #e8e4da !important;
      padding-top: 32px !important;
    }
    div[style*="paddingRight: 48"] { padding-right: 0 !important; }
  }

  @media (max-width: 600px) {
    h1[style] { font-size: clamp(32px, 10vw, 52px) !important; }
  }
`;